import { useNavigate } from '@tanstack/react-router';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { cn } from 'cpool-components/utils';
import { LOAN_REQUEST_TAGS } from 'cpool-constants';
import type {
    SchemaLoanRequestResolver_Find,
    SchemaLoanRequestResolver_GetDuplicateInfoByLead,
    SchemaLoanRequestResolver_GetSummary,
} from 'cpool-tables/backend-resolvers';
import { t } from 'i18next';
import { LuFiles, LuMail, LuPhone } from 'react-icons/lu';
import { MdOutlineWhatsapp } from 'react-icons/md';
import { toast } from 'react-toastify';

import { LEAD_LIST_ROW_HIGHLIGHT } from '../../rowHighlight';
import { type RowHighlightProps, useRowHighlightDialog } from '../../useRowHighlightDialog';
import { DuplicateModal } from '~/components/DuplicateModal';
import { ChatEntryDisabledTooltip } from '~/features/whatsapp/components/ChatEntryDisabledTooltip';
import { useLeadChatEntryState } from '~/features/whatsapp/hooks/useLeadChatEntryState';
import { useOpenLeadWhatsAppChat } from '~/features/whatsapp/hooks/useOpenLeadWhatsAppChat';
import { CHAT_ENTRY_STATES } from '~/features/whatsapp/utils/resolveChatEntryState';
import { formatPhoneNumber } from '~/utils/phoneFormatter';

type Props = {
    lead:
        | SchemaLoanRequestResolver_GetSummary[number]
        | (SchemaLoanRequestResolver_Find[number] & {
              antragsteller1: NonNullable<SchemaLoanRequestResolver_Find[number]['antragsteller1']>;
          })
        | SchemaLoanRequestResolver_GetDuplicateInfoByLead[number];
    showOnlyDuplicateInfo?: boolean;
} & RowHighlightProps;

export const ActionButtons: React.FC<Props> = ({ lead, showOnlyDuplicateInfo = false, ...highlightProps }) => {
    const {
        antragsteller1: {
            personendaten: { email, telefonPrivat },
        },
    } = lead;
    const navigate = useNavigate();
    const { isStarting, open: openLeadWhatsAppChat } = useOpenLeadWhatsAppChat(lead);
    const chatEntryState = useLeadChatEntryState(lead.id);
    const { dialogOpen: duplicateModalOpen, setDialogOpen: setDuplicateModalOpen } = useRowHighlightDialog(
        LEAD_LIST_ROW_HIGHLIGHT.DUPLICATE,
        highlightProps,
    );
    const hasDuplicates =
        lead.filters.includes(LOAN_REQUEST_TAGS.DUPLICATE) || lead.filters.includes(LOAN_REQUEST_TAGS.STOCK);

    const openEmailApp = () => {
        if (!email) {
            toast.error(t('MESSAGE.EMAIL.NO_EMAIL'));
            return;
        }
        window.open(`mailto:${email}`, '_blank', 'noopener,noreferrer');
    };

    const openCallApp = () => {
        if (!telefonPrivat) {
            toast.error(t('MESSAGE.CALL.NO_PHONE'));
            return;
        }
        navigate({
            href: `tel:${formatPhoneNumber(telefonPrivat)}`,
            reloadDocument: true,
        });
    };

    return (
        <>
            <div
                className="grid grid-cols-2 grid-rows-2 place-items-center place-content-center gap-2 z-10 ml-4 w-[60px]"
                onClick={(evt) => {
                    evt.preventDefault();
                    evt.stopPropagation();
                }}
            >
                {!showOnlyDuplicateInfo && (
                    <>
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            data-testid="phone-button"
                            onClick={openCallApp}
                            disabled={!telefonPrivat}
                            iconLeft={<LuPhone className="text-md text-green-600 " />}
                        ></Button>
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            data-testid="email-button"
                            onClick={openEmailApp}
                            disabled={!email}
                            iconLeft={<LuMail className="text-md " />}
                        ></Button>
                        {(chatEntryState === CHAT_ENTRY_STATES.ENABLED ||
                            chatEntryState === CHAT_ENTRY_STATES.ERROR) && (
                            <ChatEntryDisabledTooltip state={chatEntryState}>
                                <Button
                                    variant={ButtonVariant.SECONDARY}
                                    data-testid="whatsapp-button"
                                    onClick={openLeadWhatsAppChat}
                                    className="text-midnight"
                                    disabled={isStarting || chatEntryState !== CHAT_ENTRY_STATES.ENABLED}
                                    iconLeft={<MdOutlineWhatsapp className="text-md " />}
                                ></Button>
                            </ChatEntryDisabledTooltip>
                        )}
                    </>
                )}

                <Button
                    variant={ButtonVariant.SECONDARY}
                    data-testid="duplicate-modal-button"
                    onClick={() => {
                        setDuplicateModalOpen(true);
                    }}
                    disabled={!hasDuplicates}
                    iconLeft={<LuFiles className={cn('text-md ', hasDuplicates && 'text-red-500')} />}
                ></Button>
            </div>
            <div
                onClick={(evt) => {
                    evt.stopPropagation();
                }}
            >
                <DuplicateModal
                    open={duplicateModalOpen}
                    onClose={() => {
                        setDuplicateModalOpen(false);
                    }}
                    loanRequestId={lead.id}
                />
            </div>
        </>
    );
};
